import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Tag as TagIcon, Ruler, MousePointer, Loader2, Cog, Maximize2,
  ZoomIn, ZoomOut, Orbit, Plane, Globe, Trash2, Pencil, ExternalLink,
  Eye, Box as BoxIcon, Square as SquareIcon,
  ArrowUp, ArrowDown, ArrowLeft as ArrowLeftIcon, ArrowRight,
  Crosshair, MoveVertical, Hexagon, Triangle, Radio,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { useAuthStore } from "../stores/authStore";
import { useAppStore } from "../stores/appStore";
import { canAdminOrg } from "../lib/permissions";
import {
  type PointCloud,
  type PointCloudTag,
  deleteTag,
  getPointCloud,
  getViewUrl,
  getApiBaseUrl,
  listTagsForPointCloud,
  reprocessPointCloud,
} from "../lib/pointCloudsApi";
import {
  PotreeViewer,
  type PotreeViewerHandle,
  type PotreeNavigationMode,
  type PotreeMeasureType,
  type PotreeCameraView,
  type PotreeProjection,
} from "../components/point-clouds/PotreeViewer";
import { PlaceTagModal } from "../components/point-clouds/PlaceTagModal";
import { EditTagModal } from "../components/point-clouds/EditTagModal";
import { PlaceSensorTagModal } from "../components/instrumentation/PlaceSensorTagModal";
import { SensorTagsList } from "../components/instrumentation/SensorTagsList";
import { type AssetOption } from "../components/instrumentation/SearchableAssetPicker";
import {
  type SensorTag,
  deleteSensorTag,
  listSensorTags,
} from "../lib/instrumentationApi";
import api from "../lib/api";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import {
  type AssetMaintenanceSummary,
  MAINTENANCE_STATE_TOKENS,
  fetchSiteMaintenanceByAsset,
} from "../lib/maintenanceApi";
import { formatDate } from "../lib/utils";

type Tool = "navigate" | "tag" | "measure" | "sensor";

// Untyped vec3 — THREE is loaded as a window global by PotreeViewer, so we
// can't import the TS types here without forcing every consumer to also
// install three's types into this file.
type Vec3 = { x: number; y: number; z: number; clone?: () => Vec3; distanceTo?: (b: Vec3) => number };

export default function PointCloudViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Tag id deep-linked via `?tag=…` (e.g. from the asset detail page or the
  // assets table's "Open in 3D" link). Auto-selected and focused once Potree
  // has rendered its first frame.
  const requestedTagId = searchParams.get("tag");
  // Sensor-tag id deep-linked via `?sensor_tag=…` — issued by the
  // SensorDetailDrawer "View in 3D" link so the camera flies to the sensor's
  // 3D marker instead of dumping the user at the default view.
  const requestedSensorTagId = searchParams.get("sensor_tag");
  const autoFocusedRef = useRef(false);
  const autoFocusedSensorRef = useRef(false);
  const viewerRef = useRef<PotreeViewerHandle>(null);
  const { user, organizations } = useAuthStore();
  const { selectedOrgId } = useAppStore();
  const isAdmin = canAdminOrg(user, organizations, selectedOrgId);

  const [pc, setPc] = useState<PointCloud | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [tags, setTags] = useState<PointCloudTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // True once the viewer has rendered its first frame. The full-canvas
  // loading overlay hides at that point.
  const [cloudReady, setCloudReady] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{ loaded: number; total: number }>({ loaded: 0, total: 0 });

  const [tool, setTool] = useState<Tool>("navigate");
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [navMode, setNavMode] = useState<PotreeNavigationMode>("orbit");
  const [fov, setFov] = useState(60);
  const [pendingPick, setPendingPick] = useState<{ x: number; y: number; z: number } | null>(null);
  // Which Potree measurement type the user last armed. Used to highlight the
  // active button in the measure sub-toolbar; Potree itself owns the actual
  // pointer/render flow once a type is started.
  const [activeMeasure, setActiveMeasure] = useState<PotreeMeasureType | null>(null);
  // Views popover (top/bottom/left/right/front/back + perspective/ortho).
  // The popover is opened from the floating control stack; closed when the
  // user picks a direction or clicks outside.
  const [viewsOpen, setViewsOpen] = useState(false);
  const [projection, setProjection] = useState<PotreeProjection>("perspective");
  const [missNotice, setMissNotice] = useState<string | null>(null);
  const missTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Map of tag.id → marker object — see DigitalTwinViewer for the diff
  // pattern; we keep stable mesh refs across tag-list refreshes.
  const tagMarkersRef = useRef<Map<string, unknown>>(new Map());
  const lastViewerKey = useRef<string>("");
  // Parallel sensor-tag bookkeeping so the asset/sensor diffs don't collide.
  const sensorMarkersRef = useRef<Map<string, unknown>>(new Map());
  const [sensorTags, setSensorTags] = useState<SensorTag[]>([]);
  const [pendingSensorPick, setPendingSensorPick] = useState<{ x: number; y: number; z: number } | null>(null);
  const [selectedSensorTagId, setSelectedSensorTagId] = useState<string | null>(null);
  // Sidebar tab — flipped to match the top toolbar's active Tag tool so
  // the sidebar always reflects the user's current intent.
  const [sidebarTab, setSidebarTab] = useState<"assets" | "sensors">("assets");
  const [deletingSensorTagId, setDeletingSensorTagId] = useState<string | null>(null);

  // Load point cloud metadata + tags + view URL.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    setIsLoading(true);
    setLoadError(null);

    async function load() {
      try {
        const [cloud, tagList] = await Promise.all([
          getPointCloud(id!),
          listTagsForPointCloud(id!),
        ]);
        if (cancelled) return;
        setPc(cloud);
        setTags(tagList.tags || []);

        if (cloud.status === "processing" || cloud.status === "uploading") {
          // Server is still extracting. Show the processing banner and poll.
          setViewUrl(null);
          setLoadError(null);
          setIsLoading(false);
          pollTimer = setTimeout(load, 5000);
          return;
        }
        if (cloud.status === "error") {
          const err = cloud.extraction_metadata?.error_message;
          setLoadError(
            err
              ? `Extraction failed: ${err}`
              : "Extraction failed on the server. Try reprocessing or re-upload the .zip.",
          );
          setIsLoading(false);
          return;
        }

        const view = await getViewUrl(id!);
        if (cancelled) return;
        setCloudReady(false);
        setDownloadProgress({ loaded: 0, total: 0 });
        if (!view.view_url) {
          setLoadError(
            cloud.potree_root_s3_key
              ? "Viewer URL could not be issued."
              : "This point cloud has no extracted Potree tree yet. Re-upload the source .zip.",
          );
          setViewUrl(null);
        } else {
          setViewUrl(getApiBaseUrl() + view.view_url);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const e = err as { response?: { status?: number; data?: { detail?: string } } };
        if (e.response?.status === 404) {
          setLoadError("Point cloud not found, or you don't have access to this site.");
        } else {
          setLoadError(e.response?.data?.detail || "Failed to load point cloud.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [id]);

  const refreshTags = useCallback(() => {
    if (!id) return;
    listTagsForPointCloud(id).then((res) => setTags(res.tags || [])).catch(() => {});
  }, [id]);

  const refreshSensorTags = useCallback(() => {
    if (!id) return;
    listSensorTags({ point_cloud_id: id })
      .then((res) => setSensorTags(res))
      .catch(() => setSensorTags([]));
  }, [id]);
  useEffect(() => {
    refreshSensorTags();
  }, [refreshSensorTags]);

  // Same-site assets for the searchable picker rendered inside the
  // expanded sensor-tag row.
  const [siteAssets, setSiteAssets] = useState<AssetOption[]>([]);
  useEffect(() => {
    const siteId = pc?.virtual_site_id;
    if (!siteId) {
      setSiteAssets([]);
      return;
    }
    let cancelled = false;
    api
      .get("/assets", { params: { virtual_site_id: siteId, page_size: 500 } })
      .then((res) => {
        if (cancelled) return;
        setSiteAssets((res.data?.items || []) as AssetOption[]);
      })
      .catch((err) => {
        console.error("Failed to load assets for sensor-tag asset picker:", err);
        if (!cancelled) setSiteAssets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [pc?.virtual_site_id]);

  // Per-asset maintenance summary keyed by asset_id. Mirrors the digital-twin
  // viewer's pattern: a single /maintenance/tasks fetch scoped to this point
  // cloud's site, grouped client-side. Refetched whenever the tag list
  // changes so a newly-tagged asset's maintenance state appears immediately.
  const [maintenanceByAsset, setMaintenanceByAsset] = useState<
    Map<string, AssetMaintenanceSummary>
  >(new Map());
  const refreshMaintenance = useCallback(() => {
    const siteId = pc?.virtual_site_id;
    if (!siteId) return;
    fetchSiteMaintenanceByAsset(siteId)
      .then(setMaintenanceByAsset)
      .catch(() => setMaintenanceByAsset(new Map()));
  }, [pc?.virtual_site_id]);
  useEffect(() => {
    refreshMaintenance();
  }, [refreshMaintenance, tags]);

  /** Sidebar tag click → animate the camera onto the tag's 3D position and
   *  pulse its marker. Same pattern as DigitalTwinViewer. */
  const focusTag = useCallback((tag: PointCloudTag) => {
    const g = (tag.geometry || {}) as Record<string, unknown>;
    const x = g.x as number, y = g.y as number, z = g.z as number;
    if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") return;
    const viewer = viewerRef.current;
    if (!viewer) return;
    const THREE = (window as unknown as { THREE: { Vector3: new (x: number, y: number, z: number) => Vec3 } }).THREE;
    if (!THREE) return;
    viewer.zoomToPoint(new THREE.Vector3(x, y, z));
    const marker = tagMarkersRef.current.get(tag.id);
    if (marker) viewer.highlightMarker(marker);
  }, []);

  /** Same as focusTag but for sensor tags — they have the same geometry
   *  shape but live in a separate marker map. */
  const focusSensorTag = useCallback((tag: SensorTag) => {
    const g = (tag.geometry || {}) as Record<string, unknown>;
    const x = g.x as number, y = g.y as number, z = g.z as number;
    if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") return;
    const viewer = viewerRef.current;
    if (!viewer) return;
    const THREE = (window as unknown as { THREE: { Vector3: new (x: number, y: number, z: number) => Vec3 } }).THREE;
    if (!THREE) return;
    viewer.zoomToPoint(new THREE.Vector3(x, y, z));
    const marker = sensorMarkersRef.current.get(tag.id);
    if (marker) viewer.highlightMarker(marker);
  }, []);

  const [deletingTagId, setDeletingTagId] = useState<string | null>(null);
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [tagPendingDelete, setTagPendingDelete] = useState<PointCloudTag | null>(null);
  const [tagDeleteError, setTagDeleteError] = useState<string | null>(null);
  const [tagBeingEdited, setTagBeingEdited] = useState<PointCloudTag | null>(null);

  const taggedAssetIds = useMemo(() => tags.map((t) => t.asset_id), [tags]);
  const usedTagColors = useMemo(() => tags.map((t) => t.color), [tags]);

  const handleDeleteTag = useCallback((tag: PointCloudTag) => {
    setTagDeleteError(null);
    setTagPendingDelete(tag);
  }, []);

  // Deep-link auto-focus: once Potree has rendered its first frame and the
  // requested tag's marker has been created, animate the camera onto it and
  // select it in the sidebar. Guarded by autoFocusedRef so a later tag-list
  // refresh doesn't yank the camera back.
  useEffect(() => {
    if (autoFocusedRef.current) return;
    if (!requestedTagId || !cloudReady || tags.length === 0) return;
    const tag = tags.find((t) => t.id === requestedTagId);
    if (!tag) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        autoFocusedRef.current = true;
        setSelectedTagId(tag.id);
        focusTag(tag);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [requestedTagId, cloudReady, tags, focusTag]);

  // Sensor-tag deep-link auto-focus — mirrors the asset-tag handler above
  // but waits for sensorTags and the marker map to populate. Also flips the
  // sidebar tab to "sensors" so the focused tag is visible in the side
  // panel that the user opened the URL for.
  useEffect(() => {
    if (autoFocusedSensorRef.current) return;
    if (!requestedSensorTagId || !cloudReady || sensorTags.length === 0) return;
    const tag = sensorTags.find((t) => t.id === requestedSensorTagId);
    if (!tag) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        autoFocusedSensorRef.current = true;
        setSidebarTab("sensors");
        setSelectedSensorTagId(tag.id);
        focusSensorTag(tag);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [requestedSensorTagId, cloudReady, sensorTags, focusSensorTag]);

  const cancelTagDelete = useCallback(() => {
    if (deletingTagId) return;
    setTagPendingDelete(null);
    setTagDeleteError(null);
  }, [deletingTagId]);

  const confirmTagDelete = useCallback(async () => {
    const tag = tagPendingDelete;
    if (!tag) return;
    try {
      setDeletingTagId(tag.id);
      setTagDeleteError(null);
      await deleteTag(tag.id);
      refreshTags();
      setTagPendingDelete(null);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setTagDeleteError(e.response?.data?.detail || "Failed to delete tag.");
    } finally {
      setDeletingTagId(null);
    }
  }, [tagPendingDelete, refreshTags]);

  // Render existing tags as 3D markers. Diffs against the previous set so
  // existing markers aren't re-created when the tag list refreshes.
  useEffect(() => {
    if (!viewUrl || !viewerRef.current || !cloudReady) return;
    const viewer = viewerRef.current;
    const current = tagMarkersRef.current;
    const raf = requestAnimationFrame(() => {
      const viewerKey = `${viewUrl}|${navMode}`;
      if (lastViewerKey.current !== viewerKey) {
        tagMarkersRef.current.clear();
        lastViewerKey.current = viewerKey;
      }
      const THREE = (window as unknown as { THREE: { Vector3: new (x: number, y: number, z: number) => Vec3 } }).THREE;
      if (!THREE) return;

      const seen = new Set<string>();
      for (const tag of tags) {
        const g = (tag.geometry || {}) as Record<string, unknown>;
        const x = g.x as number, y = g.y as number, z = g.z as number;
        if (tag.tag_type !== "point" || typeof x !== "number" || typeof y !== "number" || typeof z !== "number") {
          continue;
        }
        seen.add(tag.id);
        if (!current.has(tag.id)) {
          const color = parseTagColor(tag.color);
          // Asset tags render as cubes (in the tag's color) — keeps them
          // visually distinct from sensor-tag spheres at any angle.
          const obj = viewer.addPointMarker(
            new THREE.Vector3(x, y, z),
            color,
            { shape: "cube" },
          );
          (obj as { userData?: Record<string, unknown> }).userData = (obj as { userData?: Record<string, unknown> }).userData || {};
          ((obj as { userData?: Record<string, unknown> }).userData as Record<string, unknown>).tagId = tag.id;
          current.set(tag.id, obj);
        }
      }
      for (const [tagId, mesh] of current.entries()) {
        if (!seen.has(tagId)) {
          viewer.removeMarker(mesh);
          current.delete(tagId);
        }
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [tags, viewUrl, navMode, cloudReady]);

  // Sensor-tag markers — separate ID space + default cyan color so they're
  // visually distinct from asset tags. Marker.userData.sensorTagId routes a
  // click to setSelectedSensorTagId.
  useEffect(() => {
    if (!viewUrl || !viewerRef.current || !cloudReady) return;
    const viewer = viewerRef.current;
    const current = sensorMarkersRef.current;
    const raf = requestAnimationFrame(() => {
      const viewerKey = `${viewUrl}|${navMode}|sensors`;
      if (lastViewerKey.current !== viewerKey) {
        sensorMarkersRef.current.clear();
        lastViewerKey.current = viewerKey;
      }
      const THREE = (window as unknown as { THREE: { Vector3: new (x: number, y: number, z: number) => Vec3 } }).THREE;
      if (!THREE) return;
      const seen = new Set<string>();
      for (const tag of sensorTags) {
        const g = (tag.geometry || {}) as Record<string, unknown>;
        const x = g.x as number, y = g.y as number, z = g.z as number;
        if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") continue;
        seen.add(tag.id);
        if (!current.has(tag.id)) {
          const color = parseTagColor(tag.color, 0x22d3ee);
          // Sensor tags render as spheres — soft/round counterpart to the
          // asset-tag cubes.
          const obj = viewer.addPointMarker(
            new THREE.Vector3(x, y, z),
            color,
            { shape: "sphere" },
          );
          (obj as { userData?: Record<string, unknown> }).userData = (obj as { userData?: Record<string, unknown> }).userData || {};
          ((obj as { userData?: Record<string, unknown> }).userData as Record<string, unknown>).sensorTagId = tag.id;
          current.set(tag.id, obj);
        }
      }
      for (const [tagId, mesh] of current.entries()) {
        if (!seen.has(tagId)) {
          viewer.removeMarker(mesh);
          current.delete(tagId);
        }
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [sensorTags, viewUrl, navMode, cloudReady]);

  function handlePick(point: Vec3 | null) {
    if (selectedTagId) setSelectedTagId(null);
    if (selectedSensorTagId) setSelectedSensorTagId(null);
    if (!point) {
      // Both Tag tools drive a miss notice; measurements use Potree's own
      // affordances and ignore clicks that miss the cloud.
      if (tool === "tag" || tool === "sensor") {
        setMissNotice("Click landed off the point cloud — try a denser area.");
        if (missTimerRef.current) clearTimeout(missTimerRef.current);
        missTimerRef.current = setTimeout(() => setMissNotice(null), 3000);
      }
      return;
    }
    setMissNotice(null);
    if (tool === "tag") {
      setPendingPick({ x: point.x, y: point.y, z: point.z });
    } else if (tool === "sensor") {
      setPendingSensorPick({ x: point.x, y: point.y, z: point.z });
    }
    // tool === "measure": Potree's measuringTool owns the click; our pick
    // result is intentionally ignored here so the two systems don't fight.
  }

  /** Arm a Potree measurement of the given type. Potree's measuringTool then
   *  takes over canvas pointer events until the measurement completes
   *  (maxMarkers reached, polygon closed, or user presses Escape). */
  const armMeasurement = useCallback((type: PotreeMeasureType) => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.startMeasurement(type);
    setActiveMeasure(type);
  }, []);

  const clearAllMeasurements = useCallback(() => {
    viewerRef.current?.clearMeasurements();
    setActiveMeasure(null);
  }, []);

  /** Apply a directional camera preset (rotates around the focus point,
   *  preserving zoom). */
  const applyView = useCallback((dir: PotreeCameraView) => {
    viewerRef.current?.setCameraView(dir);
    setViewsOpen(false);
  }, []);

  const toggleProjection = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.toggleProjection();
    // Pull the actual mode back out of the viewer rather than guessing; if
    // Potree refused the change for some reason (rare), our UI stays honest.
    setProjection(viewer.getCameraProjection());
  }, []);

  const selectedTag = useMemo(
    () => (selectedTagId ? tags.find((t) => t.id === selectedTagId) ?? null : null),
    [selectedTagId, tags],
  );

  const handleMarkerPick = useCallback((marker: unknown) => {
    const userData = (marker as { userData?: { tagId?: string; sensorTagId?: string } })?.userData;
    const sensorTagId = userData?.sensorTagId;
    if (sensorTagId) {
      setSelectedSensorTagId(sensorTagId);
      const tag = sensorTags.find((t) => t.id === sensorTagId);
      const g = tag?.geometry as { x?: number; y?: number; z?: number } | undefined;
      const THREE = (window as unknown as { THREE?: { Vector3: new (x: number, y: number, z: number) => Vec3 } }).THREE;
      if (tag && THREE && g && typeof g.x === "number" && typeof g.y === "number" && typeof g.z === "number") {
        viewerRef.current?.zoomToPoint(new THREE.Vector3(g.x, g.y, g.z));
        viewerRef.current?.highlightMarker(marker);
      }
      return;
    }
    const tagId = userData?.tagId;
    if (!tagId) return;
    setSelectedTagId(tagId);
    const tag = tags.find((t) => t.id === tagId);
    if (tag) focusTag(tag);
  }, [tags, sensorTags, focusTag]);

  const selectedSensorTag = useMemo(
    () => (selectedSensorTagId ? sensorTags.find((t) => t.id === selectedSensorTagId) ?? null : null),
    [selectedSensorTagId, sensorTags],
  );
  const taggedSensorIds = useMemo(() => sensorTags.map((t) => t.sensor_id), [sensorTags]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="-mx-6 -my-6 flex h-[calc(100vh-4rem)] flex-col bg-surface-950"
    >
      {/* Header bar */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-surface-700/50 bg-surface-900/60 px-6 py-3 backdrop-blur-xl">
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon-sm" onClick={() => navigate("/point-clouds")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-surface-100">
              {pc?.name ?? (isLoading ? "Loading…" : "Point Cloud")}
            </h1>
            {pc && (
              <div className="flex items-center gap-2 text-xs text-surface-400">
                <span>{tags.length} asset tag{tags.length === 1 ? "" : "s"}</span>
                {pc.point_count != null && <span>· {pc.point_count.toLocaleString()} points</span>}
              </div>
            )}
          </div>
        </div>

        <div className="liquid-glass flex items-center gap-1 rounded-xl p-1">
          <ToolButton
            active={tool === "navigate"}
            onClick={() => { setTool("navigate"); setActiveMeasure(null); }}
            icon={MousePointer}
            label="Navigate"
          />
          <ToolButton
            active={tool === "tag"}
            onClick={() => {
              setTool("tag");
              setSidebarTab("assets");
              setActiveMeasure(null);
            }}
            icon={TagIcon}
            label="Asset Tag"
          />
          <ToolButton
            active={tool === "measure"}
            onClick={() => setTool("measure")}
            icon={Ruler}
            label="Measure Tag"
          />
          {/* Sensor Tag — places a sensor on the point cloud surface and
              binds it to a Sensor (under a Device) from the Instrumentation
              module. Clicking an existing sensor marker opens the live
              data panel with a line plot of recent readings. Also flips
              the right sidebar to the Sensor Tags list. */}
          <ToolButton
            active={tool === "sensor"}
            onClick={() => {
              setTool("sensor");
              setSidebarTab("sensors");
              setActiveMeasure(null);
            }}
            icon={Radio}
            label="Sensor Tag"
          />
        </div>

        <div />
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="relative flex-1">
          {(isLoading || (!cloudReady && !loadError && pc?.status === "ready" && !!viewUrl)) && (
            <LoadingOverlay
              phase={isLoading ? "metadata" : downloadProgress.loaded === 0 ? "connecting" : "downloading"}
              loaded={downloadProgress.loaded}
              total={downloadProgress.total}
            />
          )}
          {loadError && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface-950">
              <div className="max-w-md rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-6 py-4 text-center text-sm text-yellow-200">
                <Cog className="mx-auto mb-2 h-6 w-6" />
                <div>{loadError}</div>
                {pc?.status === "error" && isAdmin && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    isLoading={isReprocessing}
                    onClick={async () => {
                      if (!id) return;
                      try {
                        setIsReprocessing(true);
                        const updated = await reprocessPointCloud(id);
                        setPc(updated);
                        setLoadError(null);
                      } catch (err: unknown) {
                        const e = err as { response?: { data?: { detail?: string } } };
                        setLoadError(e.response?.data?.detail || "Reprocess failed.");
                      } finally {
                        setIsReprocessing(false);
                      }
                    }}
                  >
                    Retry extraction
                  </Button>
                )}
              </div>
            </div>
          )}
          {(pc?.status === "processing" || pc?.status === "uploading") && !loadError && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface-950">
              <div className="max-w-md rounded-xl border border-brand-500/30 bg-brand-500/10 px-6 py-5 text-center text-sm text-brand-100">
                <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-brand-300" />
                <p className="font-medium">
                  {pc?.status === "uploading" ? "Uploading…" : "Extracting Potree tree…"}
                </p>
                <p className="mt-1 text-xs text-brand-200/80">
                  Unpacking the .zip and uploading the tree to S3. Usually 30 seconds to a few minutes.
                </p>
              </div>
            </div>
          )}
          {viewUrl && !loadError && (
            <PotreeViewer
              ref={viewerRef}
              viewUrl={viewUrl}
              navigationMode={navMode}
              onPick={handlePick}
              onMarkerPick={handleMarkerPick}
              onLoadError={(msg) => setLoadError(msg)}
              onLoadProgress={(loaded, total) => setDownloadProgress({ loaded, total })}
              onReady={() => setCloudReady(true)}
              className={
                "h-full w-full " +
                (tool === "tag" || tool === "measure" || tool === "sensor"
                  ? "cursor-crosshair"
                  : "cursor-grab active:cursor-grabbing")
              }
            />
          )}

          {selectedTag && (
            <TagLabelOverlay
              key={selectedTag.id}
              tag={selectedTag}
              viewerRef={viewerRef}
              maintenance={maintenanceByAsset.get(selectedTag.asset_id) ?? null}
            />
          )}

          {/* Same leader-line + label treatment for the selected sensor
              tag — rendered in parallel with the asset overlay above. */}
          {selectedSensorTag && (
            <SensorTagLabelOverlay
              key={selectedSensorTag.id}
              tag={selectedSensorTag}
              viewerRef={viewerRef}
            />
          )}

          {/* Measurement sub-toolbar — appears when the user picks the
              Measure Tag tool. Each button arms a different Potree
              measurement type; Potree handles all subsequent input and
              rendering. The Clear button wipes every measurement in the
              scene at once. */}
          {tool === "measure" && viewUrl && !loadError && pc?.status === "ready" && (
            <div className="absolute top-4 left-1/2 z-20 -translate-x-1/2 flex items-center gap-1 rounded-xl border border-surface-700/60 bg-surface-900 p-1 shadow-xl">
              <MeasureButton
                active={activeMeasure === "point"}
                onClick={() => armMeasurement("point")}
                icon={Crosshair}
                label="Point"
                title="Drop a point and read its coordinates"
              />
              <MeasureButton
                active={activeMeasure === "distance"}
                onClick={() => armMeasurement("distance")}
                icon={Ruler}
                label="Distance"
                title="Measure the distance between two or more points"
              />
              <MeasureButton
                active={activeMeasure === "height"}
                onClick={() => armMeasurement("height")}
                icon={MoveVertical}
                label="Height"
                title="Measure the vertical distance between two points"
              />
              <MeasureButton
                active={activeMeasure === "area"}
                onClick={() => armMeasurement("area")}
                icon={Hexagon}
                label="Area"
                title="Measure the area of a closed polygon"
              />
              <MeasureButton
                active={activeMeasure === "angle"}
                onClick={() => armMeasurement("angle")}
                icon={Triangle}
                label="Angle"
                title="Measure the angle at the middle point of a 3-point pick"
              />
              <div className="mx-1 h-5 w-px bg-white/15" />
              <MeasureButton
                active={false}
                onClick={clearAllMeasurements}
                icon={Trash2}
                label="Clear"
                title="Remove every measurement from the scene"
                tone="danger"
              />
            </div>
          )}

          {tool === "measure" && !activeMeasure && viewUrl && !loadError && pc?.status === "ready" && (
            <div className="liquid-glass absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full px-4 py-2 text-xs text-surface-300">
              Pick a measurement type above, then click on the cloud
            </div>
          )}

          {tool === "measure" && activeMeasure && (
            <div className="liquid-glass absolute bottom-4 left-4 rounded-xl px-4 py-2.5 text-xs text-surface-200 shadow-xl">
              <div className="flex items-center gap-2">
                <Ruler className="h-3.5 w-3.5 text-emerald-400" />
                <span className="font-medium capitalize">{activeMeasure} measurement</span>
              </div>
              <p className="mt-1 text-[11px] text-surface-400">
                Click on the cloud to drop markers. Press Esc to finish.
              </p>
            </div>
          )}

          {tool === "tag" && !pendingPick && !isLoading && !loadError && !missNotice && (
            <div className="liquid-glass absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full px-4 py-2 text-xs text-surface-300">
              Click directly on the cloud to place a tag
            </div>
          )}

          {/* Sensor Tag prompt — hidden once the user has clicked the cloud
              (modal opens) or selected an existing sensor marker. */}
          {tool === "sensor" && !isLoading && !loadError && !pendingSensorPick && !selectedSensorTag && !missNotice && (
            <div className="liquid-glass absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full border border-cyan-500/40 px-4 py-2 text-xs text-cyan-200">
              <Radio className="h-3.5 w-3.5" />
              <span>Click the cloud to place a sensor — or click an existing sensor marker for live data.</span>
            </div>
          )}

          {missNotice && (
            <div className="liquid-glass absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border-amber-500/40 px-4 py-2 text-xs text-amber-200">
              {missNotice}
            </div>
          )}

          {/* Floating camera-control stack */}
          {viewUrl && !loadError && !isLoading && pc?.status === "ready" && (
            <div className="absolute bottom-4 right-4 z-20 flex flex-col gap-1 rounded-xl border border-white/15 bg-surface-950/95 p-1.5 shadow-xl backdrop-blur-md">
              <FloatingButton
                active={navMode === "orbit"}
                onClick={() => setNavMode("orbit")}
                icon={Orbit}
                title="Orbit mode — drag rotates around the model"
              />
              <FloatingButton
                active={navMode === "earth"}
                onClick={() => setNavMode("earth")}
                icon={Globe}
                title="Earth mode — pan over the ground plane"
              />
              <FloatingButton
                active={navMode === "fly"}
                onClick={() => setNavMode("fly")}
                icon={Plane}
                title="Fly mode — WASD + mouse-look"
              />
              <div className="mx-auto h-px w-5 bg-white/10" />
              <FloatingButton
                onClick={() => viewerRef.current?.recenter()}
                icon={Maximize2}
                title="Frame — re-fit camera to cloud"
              />
              <FloatingButton
                active={viewsOpen}
                onClick={() => setViewsOpen((v) => !v)}
                icon={Eye}
                title="Views — orthographic presets + projection toggle"
              />
              <div className="mx-auto h-px w-5 bg-white/10" />
              <FloatingButton
                onClick={() => viewerRef.current?.zoomIn()}
                icon={ZoomIn}
                title="Zoom in"
              />
              <FloatingButton
                onClick={() => viewerRef.current?.zoomOut()}
                icon={ZoomOut}
                title="Zoom out"
              />
              <div className="mx-auto h-px w-5 bg-white/10" />
              <div className="flex flex-col items-center gap-1 py-1.5" title="Field of view">
                <span className="text-[9px] font-semibold uppercase tracking-wider text-surface-300">FOV</span>
                <input
                  type="range"
                  min={20}
                  max={110}
                  step={1}
                  value={fov}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setFov(v);
                    viewerRef.current?.setFov(v);
                  }}
                  className="fov-slider h-20 w-2"
                  style={{ writingMode: "vertical-lr", direction: "rtl" }}
                />
                <span className="text-[10px] font-mono text-surface-200">{fov}°</span>
              </div>
            </div>
          )}

          {/* Views popover — anchored to the right of the floating control
              stack, opens when the Eye button is toggled. 3×2 grid of
              orthographic presets + a perspective/orthographic toggle. */}
          {viewsOpen && viewUrl && !loadError && !isLoading && pc?.status === "ready" && (
            <div className="absolute bottom-4 right-16 z-30 w-44 rounded-xl border border-white/15 bg-surface-950/95 p-3 shadow-2xl backdrop-blur-md">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-surface-300">
                  Views
                </span>
                <button
                  onClick={() => setViewsOpen(false)}
                  className="text-surface-500 hover:text-surface-200"
                  aria-label="Close views"
                >
                  <span className="text-xs">×</span>
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1">
                <DirectionButton onClick={() => applyView("top")} icon={ArrowUp} label="Top" />
                <DirectionButton onClick={() => applyView("front")} icon={ArrowUp} label="Front" rotate={45} />
                <DirectionButton onClick={() => applyView("right")} icon={ArrowRight} label="Right" />
                <DirectionButton onClick={() => applyView("left")} icon={ArrowLeftIcon} label="Left" />
                <DirectionButton onClick={() => applyView("back")} icon={ArrowDown} label="Back" rotate={45} />
                <DirectionButton onClick={() => applyView("bottom")} icon={ArrowDown} label="Bottom" />
              </div>
              <div className="my-2 h-px w-full bg-white/10" />
              <button
                onClick={toggleProjection}
                className="flex w-full items-center justify-center gap-1.5 rounded-md bg-surface-800 px-2 py-1.5 text-xs font-medium text-surface-200 transition-colors hover:bg-surface-700"
                title="Toggle perspective ↔ orthographic projection"
              >
                {projection === "perspective" ? (
                  <>
                    <BoxIcon className="h-3.5 w-3.5" />
                    Perspective
                  </>
                ) : (
                  <>
                    <SquareIcon className="h-3.5 w-3.5" />
                    Orthographic
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Side panel: Asset Tags / Sensor Tags tabs */}
        <aside className="hidden w-72 flex-shrink-0 border-l border-surface-700/50 bg-surface-900/40 lg:flex lg:flex-col">
          <div className="flex shrink-0 border-b border-surface-700/50 bg-surface-900/60">
            <SidebarTabButton
              active={sidebarTab === "assets"}
              onClick={() => setSidebarTab("assets")}
              icon={TagIcon}
              label="Asset Tags"
              count={tags.length}
            />
            <SidebarTabButton
              active={sidebarTab === "sensors"}
              onClick={() => setSidebarTab("sensors")}
              icon={Radio}
              label="Sensor Tags"
              count={sensorTags.length}
            />
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3">
            {sidebarTab === "sensors" ? (
              <SensorTagsList
                tags={sensorTags}
                selectedTagId={selectedSensorTagId}
                deletingTagId={deletingSensorTagId}
                assets={siteAssets}
                onAssetChanged={refreshSensorTags}
                onOpenInInstrumentation={(sensorId) =>
                  navigate(`/instrumentation/sensors/${sensorId}`)
                }
                onSelect={(t) => {
                  // Toggle: clicking the already-selected row collapses it.
                  if (selectedSensorTagId === t.id) {
                    setSelectedSensorTagId(null);
                    return;
                  }
                  setSelectedSensorTagId(t.id);
                  const g = t.geometry as { x?: number; y?: number; z?: number };
                  const THREE = (window as unknown as {
                    THREE?: {
                      Vector3: new (x: number, y: number, z: number) => Vec3;
                    };
                  }).THREE;
                  if (
                    THREE &&
                    typeof g.x === "number" &&
                    typeof g.y === "number" &&
                    typeof g.z === "number"
                  ) {
                    viewerRef.current?.zoomToPoint(new THREE.Vector3(g.x, g.y, g.z));
                    const marker = sensorMarkersRef.current.get(t.id);
                    if (marker) viewerRef.current?.highlightMarker(marker);
                  }
                }}
                onDelete={async (t) => {
                  try {
                    setDeletingSensorTagId(t.id);
                    await deleteSensorTag(t.id);
                    if (selectedSensorTagId === t.id) setSelectedSensorTagId(null);
                    refreshSensorTags();
                  } finally {
                    setDeletingSensorTagId(null);
                  }
                }}
              />
            ) : tags.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-surface-500">
                No asset tags yet. Switch to the Asset Tag tool and click on the cloud.
              </p>
            ) : (
              <div className="space-y-2">
              {tags.map((t) => {
                const isDeleting = deletingTagId === t.id;
                const isExpanded = selectedTagId === t.id;
                const headline = t.asset_name || t.asset_code || t.label || "Tag";
                const maint = maintenanceByAsset.get(t.asset_id) ?? null;
                return (
                  <div
                    key={t.id}
                    className={
                      "group rounded-md border transition-colors " +
                      (isExpanded
                        ? "border-brand-500/50 bg-brand-500/10"
                        : "border-surface-700/60 bg-surface-900/60 hover:border-brand-500/40 hover:bg-brand-500/5")
                    }
                  >
                    <div className="flex items-center gap-2 px-2 py-1.5">
                      <button
                        onClick={() => {
                          setSelectedTagId(t.id);
                          focusTag(t);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        title="Show details and zoom to this tag in the 3D cloud"
                      >
                        <span
                          className="h-2 w-2 shrink-0 rounded-full ring-1 ring-white/20"
                          style={{ backgroundColor: t.color || "#6366f1" }}
                        />
                        <span
                          className={
                            "min-w-0 flex-1 text-xs font-medium text-surface-100 " +
                            (isExpanded ? "break-words" : "truncate")
                          }
                        >
                          {headline}
                        </span>
                      </button>

                      {maint?.state && (
                        <MaintenanceDot
                          state={maint.state}
                          count={
                            maint.overdueCount +
                            maint.inProgressCount +
                            maint.scheduledCount
                          }
                        />
                      )}

                      <div className="flex shrink-0 items-center gap-0.5">
                        <SidebarIconButton
                          onClick={() => navigate(`/assets/${t.asset_id}`)}
                          title="Open linked asset in the register"
                          tone="open"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </SidebarIconButton>
                        {isAdmin && (
                          <SidebarIconButton
                            onClick={() => setTagBeingEdited(t)}
                            title="Edit this tag (asset, color, label)"
                            tone="edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </SidebarIconButton>
                        )}
                        {isAdmin && (
                          <SidebarIconButton
                            onClick={() => handleDeleteTag(t)}
                            disabled={isDeleting}
                            tone="danger"
                            title="Delete this tag (the asset stays in the register)"
                          >
                            {isDeleting ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </SidebarIconButton>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="space-y-1 border-t border-brand-500/20 px-3 py-2 text-[11px]">
                        {t.asset_name && (
                          <div>
                            <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">Asset</div>
                            <div className="break-words text-surface-100">{t.asset_name}</div>
                          </div>
                        )}
                        {t.asset_code && (
                          <div>
                            <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">Code</div>
                            <div className="break-words font-mono text-surface-200">{t.asset_code}</div>
                          </div>
                        )}
                        {t.label && (
                          <div>
                            <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">Label</div>
                            <div className="break-words text-surface-300">{t.label}</div>
                          </div>
                        )}
                        {maint?.state && (
                          <MaintenanceSummary summary={maint} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            )}
          </div>
        </aside>
      </div>

      {pc && (
        <PlaceTagModal
          isOpen={pendingPick !== null}
          onClose={() => setPendingPick(null)}
          pointCloudId={pc.id}
          virtualSiteId={pc.virtual_site_id}
          worldPoint={pendingPick}
          taggedAssetIds={taggedAssetIds}
          usedColors={usedTagColors}
          onCreated={() => {
            setPendingPick(null);
            refreshTags();
          }}
        />
      )}

      {pc && (
        <PlaceSensorTagModal
          isOpen={pendingSensorPick !== null}
          onClose={() => setPendingSensorPick(null)}
          virtualSiteId={pc.virtual_site_id}
          pointCloudId={pc.id}
          worldPoint={pendingSensorPick}
          taggedSensorIds={taggedSensorIds}
          onCreated={() => {
            setPendingSensorPick(null);
            refreshSensorTags();
          }}
        />
      )}

      {/* Live sensor readings render inline in the right sidebar's
          Sensor Tags tab (see SensorTagsList → InlineSensorReadout). No
          floating panel — keeps focus on the 3D model. */}

      {pc && (
        <EditTagModal
          isOpen={tagBeingEdited !== null}
          onClose={() => setTagBeingEdited(null)}
          tag={tagBeingEdited}
          virtualSiteId={pc.virtual_site_id}
          taggedAssetIds={taggedAssetIds}
          onSaved={() => {
            setTagBeingEdited(null);
            refreshTags();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={tagPendingDelete !== null}
        title="Delete tag?"
        message={
          tagPendingDelete
            ? `Delete the tag for ${tagPendingDelete.asset_code || tagPendingDelete.asset_name || tagPendingDelete.label || "this asset"}?`
            : ""
        }
        detail={
          tagDeleteError ??
          "This removes the 3D marker from the point cloud and breaks its link to the asset. The asset itself stays in the register."
        }
        confirmLabel="Delete tag"
        cancelLabel="Cancel"
        tone="destructive"
        isLoading={deletingTagId !== null}
        onConfirm={confirmTagDelete}
        onCancel={cancelTagDelete}
      />

      {!isAdmin && tool === "tag" && (
        <div className="pointer-events-none absolute top-20 left-1/2 -translate-x-1/2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-2 text-xs text-yellow-200">
          You have read-only access — tag creation requires site_admin or org_admin role.
        </div>
      )}
    </motion.div>
  );
}

function parseTagColor(input: string | null, fallback: number = 0xa855f7): number {
  if (!input) return fallback;
  const m = /^#([0-9a-fA-F]{6})$/.exec(input.trim());
  if (m) return parseInt(m[1], 16);
  return fallback;
}

/** Sidebar tab button — mirrors DigitalTwinViewer's; kept inline so each
 *  viewer can self-contain the cosmetic detail. */
function SidebarTabButton({
  active,
  onClick,
  icon: Icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof MousePointer;
  label: string;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "flex flex-1 items-center justify-center gap-1.5 border-b-2 px-2 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors " +
        (active
          ? "border-brand-400 text-brand-200"
          : "border-transparent text-surface-400 hover:text-surface-200")
      }
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      <span
        className={
          "ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold " +
          (active
            ? "bg-brand-500/30 text-brand-100"
            : "bg-surface-800 text-surface-400")
        }
      >
        {count}
      </span>
    </button>
  );
}

function SidebarIconButton({
  onClick, title, children, disabled, tone = "neutral",
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
  tone?: "neutral" | "open" | "edit" | "danger";
}) {
  const toneClass = {
    neutral: "text-surface-400 hover:bg-surface-800 hover:text-surface-100",
    open: "text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 hover:text-sky-300",
    edit: "text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 hover:text-amber-300",
    danger: "text-red-400 bg-red-500/10 hover:bg-red-500/20 hover:text-red-300",
  }[tone];
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={disabled}
      title={title}
      className={
        "flex h-6 w-6 items-center justify-center rounded-md transition-colors disabled:opacity-50 " +
        toneClass
      }
    >
      {children}
    </button>
  );
}

function FloatingButton({
  active, onClick, icon: Icon, title,
}: {
  active?: boolean;
  onClick: () => void;
  icon: typeof MousePointer;
  title: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={
        "flex h-8 w-8 items-center justify-center rounded-md transition-colors " +
        (active
          ? "bg-brand-500/20 text-brand-200"
          : "text-surface-300 hover:bg-surface-800 hover:text-surface-100")
      }
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

/** Pill-style button used in the measurement sub-toolbar. Mirrors the
 *  visual language of ToolButton but a touch smaller so the row of five
 *  measurement types + Clear fits comfortably above the canvas. */
function MeasureButton({
  active, onClick, icon: Icon, label, title, tone = "neutral",
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof MousePointer;
  label: string;
  title: string;
  tone?: "neutral" | "danger";
}) {
  const base = "flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium transition-colors";
  const variant =
    tone === "danger"
      ? "text-red-300 hover:bg-red-500/15 hover:text-red-200"
      : active
        ? "bg-emerald-500/20 text-emerald-200"
        : "text-surface-300 hover:bg-surface-800 hover:text-surface-100";
  return (
    <button onClick={onClick} title={title} className={`${base} ${variant}`}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

/** Single direction tile inside the Views popover. `rotate` lets a few
 *  presets reuse the same arrow icon at a different angle (e.g. Front/Back
 *  share the up/down arrow tilted 45°) without pulling in extra glyphs. */
function DirectionButton({
  onClick, icon: Icon, label, rotate,
}: {
  onClick: () => void;
  icon: typeof MousePointer;
  label: string;
  rotate?: number;
}) {
  return (
    <button
      onClick={onClick}
      title={`${label} view`}
      className="flex flex-col items-center justify-center gap-0.5 rounded-md bg-surface-800/60 px-2 py-1.5 text-[9px] font-medium uppercase tracking-wider text-surface-300 transition-colors hover:bg-surface-700 hover:text-surface-100"
    >
      <Icon
        className="h-3.5 w-3.5"
        style={rotate ? { transform: `rotate(${rotate}deg)` } : undefined}
      />
      {label}
    </button>
  );
}

function LoadingOverlay({
  phase, loaded, total,
}: {
  phase: "metadata" | "connecting" | "downloading";
  loaded: number;
  total: number;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : null;
  const mb = (b: number) => (b / 1024 / 1024).toFixed(1);
  const title =
    phase === "metadata"
      ? "Loading point cloud…"
      : phase === "connecting"
        ? "Connecting to storage…"
        : "Streaming Potree tiles";
  const subtitle =
    phase === "downloading"
      ? total > 0
        ? `${mb(loaded)} MB / ${mb(total)} MB`
        : `${mb(loaded)} MB loaded`
      : phase === "connecting"
        ? "Reaching the storage bucket"
        : "Fetching metadata and access tokens";
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface-950/95">
      <div className="w-full max-w-sm px-8">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-brand-300" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-surface-100">{title}</div>
            <div className="text-xs text-surface-400">{subtitle}</div>
          </div>
          {pct !== null && (
            <div className="ml-auto font-mono text-sm font-semibold text-brand-200">
              {pct}%
            </div>
          )}
        </div>
        <div className="relative mt-3 h-2 overflow-hidden rounded-full bg-surface-800/80 ring-1 ring-white/5">
          {pct !== null ? (
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-500 via-purple-500 to-brand-400 transition-[width] duration-150 ease-out"
              style={{
                width: `${pct}%`,
                boxShadow: "0 0 12px rgba(99,102,241,0.55)",
              }}
            />
          ) : (
            <div className="loading-shimmer absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-brand-500/70 to-transparent" />
          )}
        </div>
      </div>
    </div>
  );
}

function TagLabelOverlay({
  tag,
  viewerRef,
  maintenance,
}: {
  tag: PointCloudTag;
  viewerRef: RefObject<PotreeViewerHandle | null>;
  maintenance: AssetMaintenanceSummary | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<SVGLineElement>(null);
  const dotRef = useRef<SVGCircleElement>(null);
  const tipRef = useRef<SVGCircleElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  const LEADER_DX = 70;
  const LEADER_DY = -52;

  const accent = tag.color || "#a5b4fc";
  const labelName = tag.asset_name || tag.asset_code || tag.label || "Tag";

  useEffect(() => {
    const g = (tag.geometry || {}) as Record<string, unknown>;
    const x = g.x as number, y = g.y as number, z = g.z as number;
    if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") return;
    const THREE = (window as unknown as { THREE?: { Vector3: new (x: number, y: number, z: number) => unknown } }).THREE;
    if (!THREE) return;
    const worldPoint = new THREE.Vector3(x, y, z);
    let raf = 0;
    const tick = () => {
      const proj = viewerRef.current?.projectToScreen(worldPoint);
      const container = containerRef.current;
      if (proj && container) {
        if (proj.visible) {
          container.style.opacity = "1";
          const dotX = proj.x;
          const dotY = proj.y;
          const tipX = dotX + LEADER_DX;
          const tipY = dotY + LEADER_DY;
          lineRef.current?.setAttribute("x1", String(dotX));
          lineRef.current?.setAttribute("y1", String(dotY));
          lineRef.current?.setAttribute("x2", String(tipX));
          lineRef.current?.setAttribute("y2", String(tipY));
          dotRef.current?.setAttribute("cx", String(dotX));
          dotRef.current?.setAttribute("cy", String(dotY));
          tipRef.current?.setAttribute("cx", String(tipX));
          tipRef.current?.setAttribute("cy", String(tipY));
          if (labelRef.current) {
            labelRef.current.style.transform = `translate(${tipX + 6}px, ${tipY - 14}px)`;
          }
        } else {
          container.style.opacity = "0";
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [tag, viewerRef]);

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 z-20 transition-opacity duration-150"
      style={{ opacity: 0 }}
    >
      <svg className="absolute inset-0 h-full w-full" style={{ filter: "drop-shadow(0 0 4px rgba(0,0,0,0.6))" }}>
        <line ref={lineRef} stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
        {/* Anchor disc at the world-space tag position — see
            DigitalTwinViewer's matching overlay for the design rationale. */}
        <circle ref={dotRef} r="6" fill={accent} stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
        <circle ref={tipRef} r="3" fill={accent} stroke="rgba(255,255,255,0.7)" strokeWidth="1" />
      </svg>
      <div ref={labelRef} className="absolute left-0 top-0 flex flex-col items-start gap-1 will-change-transform">
        <div
          className="rounded-md border-2 bg-surface-950 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-surface-50 shadow-lg"
          style={{
            borderColor: accent,
            boxShadow: `0 0 14px ${accent}55, 0 4px 14px rgba(0,0,0,0.7)`,
          }}
        >
          {labelName}
        </div>
        {maintenance?.state && maintenance.primaryTask && (
          <div
            className={
              "rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shadow-lg " +
              MAINTENANCE_STATE_TOKENS[maintenance.state].chipClass
            }
          >
            {MAINTENANCE_STATE_TOKENS[maintenance.state].label}
            {" · "}
            {formatDate(maintenance.primaryTask.scheduled_date)}
          </div>
        )}
      </div>
    </div>
  );
}

/** Floating leader-line + label for the selected SENSOR tag. Mirrors
 *  TagLabelOverlay above (the asset-tag version). Sensor tags default
 *  to cyan if no color set — distinct from asset tags' indigo default
 *  so the two layers stay visually separable on the cloud. */
function SensorTagLabelOverlay({
  tag,
  viewerRef,
}: {
  tag: SensorTag;
  viewerRef: RefObject<PotreeViewerHandle | null>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<SVGLineElement>(null);
  const dotRef = useRef<SVGCircleElement>(null);
  const tipRef = useRef<SVGCircleElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  const LEADER_DX = 70;
  const LEADER_DY = -52;

  const accent = tag.color || "#22d3ee";
  const labelName = tag.label || tag.sensor_name || "Sensor";

  useEffect(() => {
    const g = (tag.geometry || {}) as Record<string, unknown>;
    const x = g.x as number, y = g.y as number, z = g.z as number;
    if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") return;
    // PotreeViewer ships its own bundled three.js — read THREE off `window`
    // instead of importing it so we don't pull a second instance into the
    // bundle.
    const THREE = (window as unknown as { THREE?: { Vector3: new (x: number, y: number, z: number) => unknown } }).THREE;
    if (!THREE) return;
    const worldPoint = new THREE.Vector3(x, y, z);
    let raf = 0;
    const tick = () => {
      const proj = viewerRef.current?.projectToScreen(worldPoint);
      const container = containerRef.current;
      if (proj && container) {
        if (proj.visible) {
          container.style.opacity = "1";
          const dotX = proj.x;
          const dotY = proj.y;
          const tipX = dotX + LEADER_DX;
          const tipY = dotY + LEADER_DY;
          lineRef.current?.setAttribute("x1", String(dotX));
          lineRef.current?.setAttribute("y1", String(dotY));
          lineRef.current?.setAttribute("x2", String(tipX));
          lineRef.current?.setAttribute("y2", String(tipY));
          dotRef.current?.setAttribute("cx", String(dotX));
          dotRef.current?.setAttribute("cy", String(dotY));
          tipRef.current?.setAttribute("cx", String(tipX));
          tipRef.current?.setAttribute("cy", String(tipY));
          if (labelRef.current) {
            labelRef.current.style.transform = `translate(${tipX + 6}px, ${tipY - 14}px)`;
          }
        } else {
          container.style.opacity = "0";
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [tag, viewerRef]);

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 z-20 transition-opacity duration-150"
      style={{ opacity: 0 }}
    >
      <svg className="absolute inset-0 h-full w-full" style={{ filter: "drop-shadow(0 0 4px rgba(0,0,0,0.6))" }}>
        <line ref={lineRef} stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
        {/* Anchor disc at the world-space tag position — see
            DigitalTwinViewer's matching overlay for the design rationale. */}
        <circle ref={dotRef} r="6" fill={accent} stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
        <circle ref={tipRef} r="3" fill={accent} stroke="rgba(255,255,255,0.7)" strokeWidth="1" />
      </svg>
      <div ref={labelRef} className="absolute left-0 top-0 flex flex-col items-start gap-1 will-change-transform">
        <div
          className="rounded-md border-2 bg-surface-950 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-surface-50 shadow-lg"
          style={{
            borderColor: accent,
            boxShadow: `0 0 14px ${accent}55, 0 4px 14px rgba(0,0,0,0.7)`,
          }}
        >
          {labelName}
        </div>
      </div>
    </div>
  );
}

/** Compact maintenance state chip rendered next to each sidebar tag row.
 *  Mirrors the helper in DigitalTwinViewer so both 3D viewers expose the
 *  same indicator vocabulary to the user. */
function MaintenanceDot({
  state,
  count,
}: {
  state: "overdue" | "in_progress" | "scheduled";
  count: number;
}) {
  const tokens = MAINTENANCE_STATE_TOKENS[state];
  return (
    <span
      className={
        "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider " +
        tokens.chipClass
      }
      title={`${tokens.label}: ${count} active task${count === 1 ? "" : "s"}`}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: tokens.hex }}
      />
      {count}
    </span>
  );
}

function MaintenanceSummary({ summary }: { summary: AssetMaintenanceSummary }) {
  if (!summary.state) return null;
  const tokens = MAINTENANCE_STATE_TOKENS[summary.state];
  const t = summary.primaryTask;
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">
        Maintenance
      </div>
      <div className="mt-1 space-y-1">
        <span
          className={
            "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
            tokens.chipClass
          }
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: tokens.hex }}
          />
          {tokens.label}
        </span>
        <div className="text-[10px] text-surface-400">
          {summary.overdueCount > 0 && (
            <span className="text-red-300">{summary.overdueCount} overdue</span>
          )}
          {summary.overdueCount > 0 &&
            (summary.inProgressCount > 0 || summary.scheduledCount > 0) &&
            " · "}
          {summary.inProgressCount > 0 && (
            <span className="text-blue-300">
              {summary.inProgressCount} in progress
            </span>
          )}
          {summary.inProgressCount > 0 && summary.scheduledCount > 0 && " · "}
          {summary.scheduledCount > 0 && (
            <span className="text-amber-300">
              {summary.scheduledCount} scheduled
            </span>
          )}
        </div>
        {t && (
          <div className="rounded border border-surface-700/60 bg-surface-900/50 px-2 py-1">
            <div className="break-words text-[11px] text-surface-200">
              {t.description}
            </div>
            <div className="mt-0.5 text-[10px] text-surface-500">
              Due {formatDate(t.scheduled_date)}
              {t.priority && ` · ${t.priority}`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ToolButton({
  active, onClick, icon: Icon, label, wip,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof MousePointer;
  label: string;
  /** Marks the tool as work-in-progress: still selectable, but renders a
   *  small amber WIP pip so users know the feature isn't fully wired up. */
  wip?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "relative flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors " +
        (active
          ? "bg-brand-500/20 text-brand-200"
          : "text-surface-400 hover:bg-surface-800 hover:text-surface-200")
      }
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      {wip && (
        <span
          className="ml-0.5 rounded-full border border-amber-500/40 bg-amber-500/15 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-amber-300"
          title="Work in progress"
        >
          WIP
        </span>
      )}
    </button>
  );
}
